// RCM Settings — direction D. New IA decided Oct 15 (00-ds-03-app-frame.md
// "RCM settings sub-nav (canonical)"). UI only; mock data bound here.
import type { IconKind } from '@/shared/ui';
import { PMS } from '@/features/shared/pms';

export type RcmSettingsSlug =
  | 'autopilot'
  | 'alerts'
  | 'portals'
  | 'calls'
  | 'texts-email'
  | 'fax-mail'
  | 'practice'
  | 'signatures'
  | 'pms'
  | 'pre-auths'
  | 'follow-up'
  | 'appeals'
  | 'virtual-cards'
  | 'paper'
  | 'opportunities'
  | 'payers'
  | 'how-counted';

export interface RcmSettingsItem {
  slug: RcmSettingsSlug;
  label: string;
  /** How the page renders: a built direction-D page, an existing slot component, or a placeholder. */
  render: 'page' | 'slot' | 'placeholder';
  /** Optional amber count pill in the sub-nav. */
  count?: number;
}

export interface RcmSettingsGroupDef {
  label: string;
  /** Section icon shown next to the (bold) group label in the sub-nav. */
  icon: IconKind;
  items: RcmSettingsItem[];
}

export const RCM_SETTINGS_GROUPS: RcmSettingsGroupDef[] = [
  {
    label: 'How ELVA works',
    icon: 'sparkle',
    items: [
      { slug: 'autopilot', label: 'Autopilot', render: 'page' },
      { slug: 'alerts', label: 'Alerts', render: 'page' },
    ],
  },
  {
    label: 'Channels',
    icon: 'broadcast',
    items: [
      { slug: 'portals', label: 'Payer portals', render: 'placeholder' },
      { slug: 'calls', label: 'Calls', render: 'placeholder' },
      { slug: 'texts-email', label: 'Texts & email', render: 'placeholder' },
      { slug: 'fax-mail', label: 'Fax & mail', render: 'placeholder' },
    ],
  },
  {
    label: 'Practice',
    icon: 'home',
    items: [
      { slug: 'practice', label: 'Practice details', render: 'placeholder' },
      { slug: 'signatures', label: 'Signatures', render: 'placeholder' },
      // The one settings page backed by the RCM backend rather than
      // fixtures, so the app supplies its body as a slot.
      { slug: 'pms', label: 'PMS access', render: 'slot' },
    ],
  },
  {
    label: 'Claims',
    icon: 'doc',
    items: [
      { slug: 'pre-auths', label: 'Pre-auths', render: 'slot' },
      { slug: 'follow-up', label: 'Follow-up', render: 'slot' },
      { slug: 'appeals', label: 'Appeals', render: 'slot' },
    ],
  },
  {
    label: 'Payments',
    icon: 'credit',
    items: [
      { slug: 'virtual-cards', label: 'Virtual cards', render: 'slot' },
      { slug: 'paper', label: 'Paper and fax', render: 'placeholder' },
    ],
  },
  {
    label: 'Patients',
    icon: 'team',
    items: [{ slug: 'opportunities', label: 'Opportunities', render: 'placeholder' }],
  },
  {
    label: 'Reference',
    icon: 'book',
    items: [
      { slug: 'payers', label: 'Payer rules', render: 'placeholder' },
      { slug: 'how-counted', label: "How ELVA's work is counted", render: 'placeholder' },
    ],
  },
];

export interface RcmSettingsMeta {
  group: string;
  title: string;
  lead: string;
  /** Band autosave line. Read-only pages override the default "Changes save as you make them". */
  bandLine?: string;
}

const META: Partial<Record<RcmSettingsSlug, RcmSettingsMeta>> = {
  autopilot: {
    group: 'How ELVA works',
    title: 'Autopilot',
    lead: 'What ELVA does on its own, and what still waits for your team. ELVA suggests autopilot once your team has confirmed enough of each kind of work.',
  },
  alerts: {
    group: 'How ELVA works',
    title: 'Alerts',
    lead: 'Two ways ELVA tells your team that something needs a person. Nothing else.',
  },
  portals: {
    group: 'Channels',
    title: 'Payer portals',
    lead: 'The logins ELVA uses to sign in to each payer. Sign-in codes go to ELVA, never to a person.',
  },
  calls: {
    group: 'Channels',
    title: 'Calls',
    lead: 'The number ELVA calls payers from, when it calls, and how recordings are kept.',
  },
  'texts-email': {
    group: 'Channels',
    title: 'Texts & email',
    lead: "Dentists' mobiles for approvals, texting, and the address ELVA sends secure email from.",
  },
  'fax-mail': {
    group: 'Channels',
    title: 'Fax & mail',
    lead: 'The fax number ELVA sends from and the return address printed on letters.',
  },
  practice: {
    group: 'Practice',
    title: 'Practice details',
    lead: 'Tax ID, NPIs and addresses, read from your practice management system.',
    bandLine: 'Read from your practice management system — change these there',
  },
  signatures: {
    group: 'Practice',
    title: 'Signatures',
    lead: 'The claims and appeals authorization, who signs each kind, and the signatures on file.',
  },
  pms: {
    group: 'Practice',
    title: 'PMS access',
    lead: 'How ELVA reads from and writes to your practice management system.',
    bandLine: 'Set up with the ELVA team at onboarding',
  },
  paper: {
    group: 'Payments',
    title: 'Paper and fax',
    lead: 'Incoming fax, who is allowed to send to it, and how long documents are kept.',
  },
  opportunities: {
    group: 'Patients',
    title: 'Opportunities',
    lead: 'The procedures ELVA watches on every patient and raises on their insurance page when it is worth mentioning.',
  },
  payers: {
    group: 'Reference',
    title: 'Payer rules',
    lead: 'Every rule ELVA has learned for the payers you bill, with its source. Read-only.',
    bandLine: 'Read-only · learned from the payers you bill',
  },
  'how-counted': {
    group: 'Reference',
    title: "How ELVA's work is counted",
    lead: 'The minutes a person spends on each task, used to count the time ELVA saves.',
  },
};

export function metaFor(slug: RcmSettingsSlug): RcmSettingsMeta {
  return META[slug] ?? { group: 'Settings', title: 'Settings', lead: '' };
}

/** Legacy staging slugs → new slug, for redirects. */
export const RCM_SETTINGS_LEGACY_SLUGS: Record<string, RcmSettingsSlug> = {
  'ai-dispatch-policies': 'autopilot',
  'alert-thresholds': 'alerts',
  'ai-performance': 'how-counted',
  'document-center': 'paper',
};

export const RCM_SETTINGS_ITEMS: RcmSettingsItem[] = RCM_SETTINGS_GROUPS.flatMap((g) => g.items);

export function itemFor(slug: string): RcmSettingsItem | undefined {
  return RCM_SETTINGS_ITEMS.find((i) => i.slug === slug);
}

export const RCM_SETTINGS_DEFAULT_SLUG: RcmSettingsSlug = 'autopilot';

// ── change line (owner/admin gate) ──
export const RCM_SETTINGS_CHANGE = {
  by: 'Ava Thompson',
  org: 'Lone Star Billing',
  date: 'Oct 9',
};

// ── Autopilot (rcm-05) ──
export type AutopilotPillKind = 'training' | 'on' | 'off' | 'ready';

export interface AutopilotCard {
  id: string;
  n: string;
  title: string;
  pill: AutopilotPillKind;
  on: boolean;
  /** training switch stays disabled until training is done; pre-auths always enabled */
  switchEnabled: boolean;
  status: string; // status line, {lead phrase} bolded by the renderer via leadBold
  leadBold: string; // the bold span at the start of the status line
  progress?: { confirmed: number; target: number };
  /**
   * D246: a cell (payer x kind of work) ELVA paused after a correction. Listed under the switch; turning
   * autopilot off and on does not clear it.
   */
  paused?: string[];
  /** D248: this action isn't available in the practice's PMS yet; the switch stays disabled. */
  unavailable?: string;
  note: string;
  comesToYou: [string, string, string];
  link: { label: string; href: string };
}

/** D235: no autopilot until the claims and appeals authorization is signed. Mock: signed; `?auth=unsigned` shows the gate. */
export const AUTOPILOT_GATE_TEXT =
  'Available once your claims and appeals authorization is signed.';
export function isAuthorizationSigned(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('auth')?.toLowerCase() !== 'unsigned';
  } catch {
    return true;
  }
}

export const AUTOPILOT_CARDS: AutopilotCard[] = [
  {
    // D133: pill/on/switchEnabled/leadBold/status/progress below are placeholders —
    // SettingsShell's AutopilotPage overrides all of them from the claims-review
    // store (one shared value; this card never holds its own copy).
    id: 'claims',
    n: '01',
    title: 'Claims',
    pill: 'training',
    on: false,
    switchEnabled: false,
    leadBold: '47 of 200',
    status: ' claims confirmed by your team. ELVA suggests autopilot at 200.',
    progress: { confirmed: 47, target: 200 },
    paused: [
      'Paused for Delta Dental · crowns after a corrected posting on Oct 20 · 3 more confirmations to resume.',
    ],
    note: 'Each payer and kind of procedure also needs 10 confirmed. 6 still need more. Secondary claims follow the same rules.',
    comesToYou: [
      'A decision only the practice can make',
      "A payer and kind of procedure ELVA hasn't learned yet",
      "The PMS didn't accept the update",
    ],
    link: { label: 'Open Claims to review', href: '/claims' },
  },
  {
    id: 'posting',
    n: '02',
    title: 'Posting',
    pill: 'training',
    on: false,
    switchEnabled: false,
    leadBold: '38 of 100',
    status: ' payments confirmed. ELVA suggests autopilot at 100.',
    progress: { confirmed: 38, target: 100 },
    unavailable: `Posting on autopilot isn't available in ${PMS} yet. ELVA is still testing it.`,
    note: 'Each kind of payment (takeback, interest, paid as a different procedure…) also needs 5 confirmed.',
    comesToYou: [
      'Money arrived with nothing to post, or the other way round',
      "A kind of payment ELVA hasn't learned yet",
      "An amount ELVA couldn't confirm three ways",
    ],
    link: { label: 'Open Posting', href: '/posting' },
  },
  {
    id: 'appeals',
    n: '03',
    title: 'Appeals',
    pill: 'on',
    on: true,
    switchEnabled: true,
    leadBold: 'On since Oct 12.',
    status: ' 23 appeals confirmed before that.',
    note: 'Needs the claims and appeals authorization, which Dr. Emily Carter signed Oct 1.',
    comesToYou: [
      'A clinical appeal: the treating dentist approves each letter',
      "A patient's appeal: the patient signs the payer's form",
      "A payer ELVA hasn't sent an appeal to before",
    ],
    link: { label: 'Open Denied & underpaid', href: '/denied' },
  },
  {
    id: 'preauths',
    n: '04',
    title: 'Pre-auths',
    pill: 'off',
    on: false,
    switchEnabled: true,
    // D300: placeholders — AutopilotPage computes the line from the pre-auths store (one shared value).
    leadBold: '1 of 2',
    status: ' pre-auths submitted. Your owner can turn this on after the second.',
    note: 'Which payers and procedures ELVA prepares is set in Pre-auths.',
    comesToYou: [
      'A required document is missing or too old',
      "The payer won't answer before the appointment",
      'The plan or the procedure changed',
    ],
    link: { label: 'Pre-auth settings', href: '/settings/pre-auths' },
  },
];

export const AUTOPILOT_ALWAYS: { lead: string; rest: string }[] = [
  {
    lead: "Checks every patient's insurance",
    rest: ' before each visit, and again when the appointment changes.',
  },
  {
    lead: 'Signs in to payer portals',
    rest: ' for benefits, claim status, remittances and missing pages.',
  },
  {
    lead: 'Calls payers',
    rest: " for claim status, a missing piece or a receipt check. Every call says it's ELVA and is recorded.",
  },
  {
    lead: 'Follows up on unpaid claims',
    rest: ', up to the limit in Follow-up, and resends a claim a payer lost.',
  },
  { lead: 'Reads every document', rest: ' that arrives and sends it to the page it belongs on.' },
];

export const AUTOPILOT_DECIDES: { lead: string; rest: string }[] = [
  {
    lead: 'Asking a payer to reprocess a claim or stop a payment.',
    rest: ' ELVA makes the call once you say so.',
  },
  { lead: 'Writing off a balance', rest: ', and billing a patient after a denial.' },
  {
    lead: 'The letter in a clinical appeal.',
    rest: ' The treating dentist reads and approves it, even on autopilot.',
  },
];

// ── Alerts (rcm-06, email only · D169) ──
/**
 * A person with access to this practice. `rightAway` and `morning` are the two
 * email opt-ins (D169: email only, no SMS and no mobile numbers on this page).
 * Everyone on the page has an email; people without one are simply not offered
 * in the picker.
 */
export interface AlertPerson {
  id: string;
  name: string;
  /** role or organisation shown after the name in card 01 (e.g. "Owner", "Lone Star Billing"). */
  roleOrOrg: string;
  email: string;
  /** gets the "right away" email (card 01). */
  rightAway: boolean;
  /** gets the morning summary email (card 02). */
  morning: boolean;
}

export const ALERT_PEOPLE: AlertPerson[] = [
  {
    id: 'ava',
    name: 'Ava Thompson',
    roleOrOrg: 'Lone Star Billing',
    email: 'ava@lonestarbilling.com',
    rightAway: true,
    morning: true,
  },
  {
    id: 'helen',
    name: 'Helen Ortiz',
    roleOrOrg: 'Office manager',
    email: 'helen@alamoheightsdental.com',
    rightAway: true,
    morning: true,
  },
  {
    id: 'emily',
    name: 'Dr. Emily Carter',
    roleOrOrg: 'Owner',
    email: 'ecarter@alamoheightsdental.com',
    rightAway: false,
    morning: true,
  },
];

/**
 * Other people with access to this practice who aren't listed yet. "Add someone"
 * (both cards) offers these; choosing one adds a row with its Switch on.
 */
export const ALERT_PEOPLE_WITH_ACCESS: AlertPerson[] = [
  {
    id: 'ryan',
    name: 'Ryan Cole',
    roleOrOrg: 'Lone Star Billing',
    email: 'ryan@lonestarbilling.com',
    rightAway: false,
    morning: false,
  },
  {
    id: 'olivia',
    name: 'Dr. Olivia Brooks',
    roleOrOrg: 'General dentist',
    email: 'obrooks@alamoheightsdental.com',
    rightAway: false,
    morning: false,
  },
  {
    id: 'maria',
    name: 'Maria Delgado',
    roleOrOrg: 'Front desk',
    email: 'maria@alamoheightsdental.com',
    rightAway: false,
    morning: false,
  },
];

export const ALERT_EMAIL_TIME = '8:00 AM';
/** 30-minute steps across the day, US 12-hour format. */
export const ALERT_EMAIL_TIMES: string[] = (() => {
  const out: string[] = [];
  for (let m = 0; m < 24 * 60; m += 30) {
    const h24 = Math.floor(m / 60);
    const min = m % 60;
    const ap = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    out.push(`${String(h12)}:${String(min).padStart(2, '0')} ${ap}`);
  }
  return out;
})();

/**
 * Last change to the alert settings. D165: always a practice owner or admin,
 * never billing-company staff (who read settings only).
 */
export const ALERT_CHANGE = { by: 'Dr. Emily Carter', role: 'Owner', date: 'Oct 9' };
